import superagent from 'superagent';
import streamToArray from 'stream-to-array';
import getEnvironmentVariables from '../../../utilities/getEnvironmentVariables';
import logger from '../../../utilities/logger';
import getCallableFirebaseFunction from '../../../utilities/getCallableFirebaseFunction';

const { TODESKTOP_CLI_S3_BUCKET } = getEnvironmentVariables();
const MAX_RETRIES = 3;
const RETRY_DELAY = 2000; // 2 seconds

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

type UploadParams = {
  bucket?: string;
  inputStream: NodeJS.ReadableStream;
  key: string;
  onProgress?: (event: { loaded: number; total: number }) => void;
};

type UploadedResponse = {
  bucket: string;
  key: string;
  url: string;
};

const uploadToS3WithRetry = async (
  input: UploadParams,
  retries = MAX_RETRIES,
): Promise<any> => {
  try {
    return await uploadToS3(input);
  } catch (error) {
    if (retries > 0) {
      logger.warn(
        `Upload failed. Retrying... (${
          MAX_RETRIES - retries + 1
        } of ${MAX_RETRIES})`,
      );
      await delay(RETRY_DELAY);
      return uploadToS3WithRetry(input, retries - 1);
    } else {
      logger.error('All retries exhausted. Upload failed.');
      throw error;
    }
  }
};

const uploadToS3 = async ({
  bucket = TODESKTOP_CLI_S3_BUCKET,
  inputStream,
  key,
  onProgress = () => {},
}: UploadParams): Promise<UploadedResponse> => {
  const getSignedURL = getCallableFirebaseFunction('getSignedURL');
  const result = await getSignedURL({
    key,
  });
  const data = result.data as {
    signedURL: string;
    uploadURL: string;
  };

  const parts = await streamToArray(inputStream);
  const buffers = parts.map((part: Buffer | string) =>
    Buffer.isBuffer(part) ? part : Buffer.from(part),
  );
  const body = Buffer.concat(buffers);

  return new Promise<UploadedResponse>((resolve, reject) => {
    superagent
      .put(data.signedURL)
      .send(body)
      .set('content-type', 'application/zip')
      .on('progress', onProgress)
      .end((err: Error | null) => {
        logger.debug({ bucket, key, url: data.uploadURL }, 'uploadToS3');
        if (err) {
          logger.error({ err }, 'uploadToS3');
          return reject(err);
        }
        return resolve({ bucket, key, url: data.uploadURL });
      });
  });
};

export default uploadToS3WithRetry;
